import { useMemo, type ReactElement, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiClient, ApiRequestError, TokenStore } from '@proctoring/api-client';
import { API_BASE_URL, SESSION_STORAGE_KEY } from './api/config.js';
import { AuthProvider } from './auth/AuthProvider.js';

/**
 * A query client tuned for admin screens, which are read-mostly and edited deliberately.
 *
 * Auth and permission failures are never retried: a 401 means the session is gone and a
 * 403 will not become a 200, so retrying only delays the error the admin needs to see.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          if (error instanceof ApiRequestError) return false;
          return failureCount < 2;
        },
      },
      mutations: { retry: false },
    },
  });
}

export function createApiClient(tokens?: TokenStore): ApiClient {
  const store =
    tokens ??
    new TokenStore({
      storageKey: SESSION_STORAGE_KEY,
      storage: globalThis.localStorage,
    });
  // `backoffice`: the API refuses a non-admin this audience outright, so a candidate's or
  // interviewer's credentials never mint a token here at all. The role check in LoginPage
  // stays as defence in depth for a token acquired some other way.
  return new ApiClient({
    baseUrl: API_BASE_URL,
    tokens: store,
    audience: 'backoffice',
    // See the portal client: clearing the store on a rejected token is what sends an expired
    // session back to /login instead of leaving every page stuck on an error it cannot act on.
    onUnauthorized: () => {
      store.clear();
    },
  });
}

export interface AppProvidersProps {
  api?: ApiClient;
  queryClient?: QueryClient;
  children: ReactNode;
}

/**
 * Composes the query client and auth provider. Both dependencies are injectable so tests
 * can mount real pages against a mocked API and a fresh cache.
 */
export function AppProviders({ api, queryClient, children }: AppProvidersProps): ReactElement {
  const client = useMemo(() => queryClient ?? createQueryClient(), [queryClient]);
  const apiClient = useMemo(() => api ?? createApiClient(), [api]);

  return (
    <QueryClientProvider client={client}>
      <AuthProvider api={apiClient}>{children}</AuthProvider>
    </QueryClientProvider>
  );
}
